import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { formatRupees, formatRupeesCompact, formatDate, todayISO, INVESTMENT_TYPE_LABEL } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Plus, Filter, X, ArrowDownLeft, ArrowUpRight, Banknote, Wallet, Pencil, Trash2, Search, Upload, Download, Scale, Loader2, Check } from "lucide-react";
import FYPicker, { currentFYStart, fyToRange } from "@/components/FYPicker";
import MonthPicker, { monthToRange } from "@/components/MonthPicker";

const emptyForm = () => ({
  date: todayISO(), direction: "in", amount: "",
  account_id: "", category_id: "", sub_category_id: "", investment_id: "",
  counterparty: "", reference: "", note: "",
});

const KindPill = ({ kind }) => {
  const map = {
    income: "bg-green-50 text-green-700 border-green-200",
    expense: "bg-slate-100 text-slate-700 border-slate-200",
    transfer: "bg-blue-50 text-blue-700 border-blue-200",
  };
  return <span className={`text-[9px] font-medium tracking-[0.14em] uppercase px-1.5 py-0.5 rounded border ${map[kind] || ""}`}>{kind}</span>;
};

export default function CashBook() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "accounts";

  const [accounts, setAccounts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subs, setSubs] = useState([]);
  const [investments, setInvestments] = useState([]);
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const [filter, setFilter] = useState({ account_id: "", category_id: "", investment_id: "", direction: "" });
  const [fy, setFy] = useState(currentFYStart());
  const [month, setMonth] = useState("");
  const [q, setQ] = useState("");
  const [dlg, setDlg] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  // Project-scoped master lists for the entry dialog
  const [dlgAccounts, setDlgAccounts] = useState([]);
  const [dlgCategories, setDlgCategories] = useState([]);
  const [dlgSubs, setDlgSubs] = useState([]);
  const [importOpen, setImportOpen] = useState(false);
  const [reconcileFor, setReconcileFor] = useState(null); // account object

  const load = async () => {
    const [a, c, i, s] = await Promise.all([
      api.get("/accounts"),
      api.get("/categories"),
      api.get("/investments"),
      api.get("/book/summary"),
    ]);
    setAccounts(a.data); setCategories(c.data); setInvestments(i.data); setSummary(s.data);
  };

  const loadEntries = async () => {
    const params = {};
    Object.entries(filter).forEach(([k, v]) => { if (v) params[k] = v; });
    if (month) {
      const { from, to } = monthToRange(month);
      if (from) params.from_date = from;
      if (to) params.to_date = to;
    } else {
      const { from, to } = fyToRange(fy);
      if (from) params.from_date = from;
      if (to) params.to_date = to;
    }
    const r = await api.get("/book/entries", { params });
    setEntries(r.data);
  };

  useEffect(() => { load().then(() => setLoading(false)); }, []);
  useEffect(() => { if (!loading) loadEntries(); /* eslint-disable-next-line */ }, [filter, fy, month, loading]);

  // Reload project-scoped master lists whenever the entry dialog opens or its project changes
  useEffect(() => {
    if (!dlg) return;
    const params = form.investment_id ? { investment_id: form.investment_id } : {};
    Promise.all([
      api.get("/accounts", { params }),
      api.get("/categories", { params }),
      api.get("/subcategories", { params }),
    ]).then(([a, c, s]) => {
      setDlgAccounts(a.data || []);
      setDlgCategories(c.data || []);
      setDlgSubs(s.data || []);
    }).catch(() => {});
  }, [dlg, form.investment_id]);

  const catMap = useMemo(() => Object.fromEntries(categories.map((c) => [c.id, c])), [categories]);
  const accMap = useMemo(() => Object.fromEntries(accounts.map((a) => [a.id, a])), [accounts]);
  const invMap = useMemo(() => Object.fromEntries(investments.map((i) => [i.id, i])), [investments]);

  const filteredEntries = useMemo(() => {
    const s = q.toLowerCase().trim();
    if (!s) return entries;
    return entries.filter((e) =>
      (e.counterparty || "").toLowerCase().includes(s) ||
      (e.note || "").toLowerCase().includes(s) ||
      (e.reference || "").toLowerCase().includes(s) ||
      (invMap[e.investment_id]?.name || "").toLowerCase().includes(s) ||
      (catMap[e.category_id]?.name || "").toLowerCase().includes(s)
    );
  }, [entries, q, invMap, catMap]);

  const openAdd = () => { setEditing(null); setForm(emptyForm()); setDlg(true); };
  const openEdit = (e) => {
    setEditing(e);
    setForm({
      date: e.date, direction: e.direction, amount: e.amount,
      account_id: e.account_id || "", category_id: e.category_id || "",
      investment_id: e.investment_id || "",
      counterparty: e.counterparty || "", reference: e.reference || "", note: e.note || "",
    });
    setDlg(true);
  };

  const save = async () => {
    if (!form.account_id) return toast.error("Choose an account");
    if (!form.investment_id) return toast.error("Project is required for every cash entry");
    if (!form.amount || Number(form.amount) <= 0) return toast.error("Enter a valid amount");
    const payload = {
      ...form,
      amount: Number(form.amount),
      category_id: form.category_id || null,
      investment_id: form.investment_id,
    };
    try {
      if (editing) await api.put(`/book/entries/${editing.id}`, payload);
      else await api.post("/book/entries", payload);
      toast.success(editing ? "Entry updated" : "Entry saved");
      setDlg(false);
      await Promise.all([load(), loadEntries()]);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    await api.delete(`/transactions/${id}`);
    toast.success("Deleted");
    await Promise.all([load(), loadEntries()]);
  };

  const totalBankBalance = accounts.filter((a) => a.type === "bank").reduce((a, x) => a + (x.balance || 0), 0);
  const totalCashBalance = accounts.filter((a) => a.type === "cash").reduce((a, x) => a + (x.balance || 0), 0);

  if (loading) return <div className="text-slate-500 text-sm">Loading…</div>;

  const clearFilters = () => { setFilter({ account_id: "", category_id: "", investment_id: "", direction: "" }); setQ(""); setMonth(""); };
  const activeFilterCount = Object.values(filter).filter(Boolean).length + (q ? 1 : 0) + (month ? 1 : 0);

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="eyebrow">Money Flow</div>
          <h1 className="mt-2 font-editorial font-light text-[38px] leading-[1.02] sm:text-5xl md:text-6xl text-slate-900 tracking-[-0.035em]" data-testid="cashbook-title">
            Cash &amp; <em className="italic">Bank.</em>
          </h1>
          <p className="mt-2 text-sm text-slate-500 max-w-md">
            Every rupee that moves through MARKSAM — tagged to the project it belongs to.
          </p>
        </div>
        {canEdit && (
          <div className="flex items-center gap-2 flex-wrap">
            <a
              href={`${window.location.origin.replace(':3000', '')}/api/book/import/template.xlsx`}
              onClick={(e) => {
                e.preventDefault();
                const t = localStorage.getItem("mv_token");
                fetch(e.currentTarget.href, { headers: { Authorization: `Bearer ${t}` } })
                  .then(r => r.blob()).then(b => {
                    const u = URL.createObjectURL(b); const a = document.createElement("a");
                    a.href = u; a.download = "MARKSAM-bank-import-template.xlsx"; a.click();
                  });
              }}
              data-testid="download-template"
              className="inline-flex items-center gap-2 text-[13px] font-medium text-slate-700 hover:text-slate-900 border border-slate-300 hover:border-slate-400 rounded-md px-3 py-2 bg-white"
            >
              <Download className="h-4 w-4" strokeWidth={1.75} /> Template
            </a>
            <Button onClick={() => setImportOpen(true)} variant="outline" className="rounded-md gap-2 h-auto py-2" data-testid="import-btn">
              <Upload className="h-4 w-4" /> Import statement
            </Button>
            <Link to="/receipts/new">
              <Button className="rounded-md bg-green-700 hover:bg-green-800 text-white gap-2" data-testid="add-receipt-btn">
                <ArrowDownLeft className="h-4 w-4" /> Receipt
              </Button>
            </Link>
            <Link to="/payments/new">
              <Button className="rounded-md bg-red-600 hover:bg-red-700 text-white gap-2" data-testid="add-payment-btn">
                <ArrowUpRight className="h-4 w-4" /> Payment
              </Button>
            </Link>
          </div>
        )}
      </div>

      {/* Account balance cards */}
      <div>
        <div className="eyebrow mb-3">Balances</div>
        <div className="grid gap-3 grid-cols-2 md:grid-cols-3 xl:grid-cols-4">
          {accounts.map((a) => (
            <div
              key={a.id}
              data-testid={`account-${a.id}`}
              className={`text-left border rounded-md p-4 bg-white hover-lift transition-colors ${filter.account_id === a.id ? "border-red-600 ring-1 ring-red-600" : "border-slate-200"}`}
            >
              <button
                onClick={() => setFilter({ ...filter, account_id: a.id === filter.account_id ? "" : a.id })}
                className="w-full text-left"
              >
                <div className="flex items-center gap-2">
                  {a.type === "bank"
                    ? <Banknote className="h-4 w-4 text-slate-500" strokeWidth={1.75} />
                    : <Wallet className="h-4 w-4 text-slate-500" strokeWidth={1.75} />}
                  <span className="text-[10px] tracking-[0.2em] uppercase text-slate-500">{a.bank_name || a.type}</span>
                </div>
                <div className="mt-1 text-[13px] font-medium text-slate-900 truncate">
                  {a.name} {a.account_number && <span className="text-slate-400 font-normal">· {a.account_number}</span>}
                </div>
                <div className="mt-2 font-mono-fin text-xl text-slate-900 font-medium">
                  {formatRupeesCompact(a.balance)}
                </div>
                <div className="mt-1 flex items-center gap-3 text-[10px]">
                  <span className="text-green-700">↓ {formatRupeesCompact(a.total_in)}</span>
                  <span className="text-red-600">↑ {formatRupeesCompact(a.total_out)}</span>
                </div>
              </button>
              {canEdit && (
                <button
                  onClick={() => setReconcileFor(a)}
                  data-testid={`reconcile-${a.id}`}
                  className="mt-3 pt-3 border-t border-slate-100 w-full text-left inline-flex items-center gap-1.5 text-[11px] text-slate-500 hover:text-slate-900"
                >
                  <Scale className="h-3 w-3" /> Reconcile
                </button>
              )}
            </div>
          ))}
        </div>
        <div className="mt-3 flex flex-wrap gap-4 text-xs text-slate-500">
          <span>Bank total: <span className="font-mono-fin font-medium text-slate-900">{formatRupees(totalBankBalance)}</span></span>
          <span>Cash total: <span className="font-mono-fin font-medium text-slate-900">{formatRupees(totalCashBalance)}</span></span>
        </div>
      </div>

      {/* Summary bands (by investment + by category) */}
      {summary && (
        <div className="grid gap-4 md:grid-cols-2">
          <div className="border border-slate-200 rounded-md bg-white p-5">
            <div className="eyebrow">By investment</div>
            <div className="mt-3 space-y-2">
              {summary.by_investment.length === 0 && (
                <div className="text-xs text-slate-500">No investment-tagged entries yet.</div>
              )}
              {summary.by_investment.slice(0, 6).map((r) => (
                <button
                  key={r.investment_id}
                  onClick={() => setFilter({ ...filter, investment_id: r.investment_id === filter.investment_id ? "" : r.investment_id })}
                  className="w-full flex items-center justify-between gap-2 py-1.5 border-b border-slate-100 last:border-0 hover:bg-slate-50 -mx-2 px-2 rounded"
                >
                  <span className="text-[13px] truncate">{r.name}</span>
                  <span className="flex items-center gap-2 shrink-0 text-[11px] font-mono-fin">
                    <span className="text-green-700">↓ {formatRupeesCompact(r.in)}</span>
                    <span className="text-red-600">↑ {formatRupeesCompact(r.out)}</span>
                  </span>
                </button>
              ))}
              {summary.untagged.count > 0 && (
                <div className="pt-2 mt-2 border-t border-slate-200 flex items-center justify-between text-[11px]">
                  <span className="text-slate-500">Untagged ({summary.untagged.count})</span>
                  <span className="font-mono-fin">
                    <span className="text-green-700">↓ {formatRupeesCompact(summary.untagged.in)}</span>
                    <span className="text-red-600 ml-2">↑ {formatRupeesCompact(summary.untagged.out)}</span>
                  </span>
                </div>
              )}
            </div>
          </div>
          <div className="border border-slate-200 rounded-md bg-white p-5">
            <div className="eyebrow">By category</div>
            <div className="mt-3 space-y-2">
              {summary.by_category.length === 0 && (
                <div className="text-xs text-slate-500">No categorised entries yet.</div>
              )}
              {summary.by_category.slice(0, 6).map((r) => (
                <button
                  key={r.category_id}
                  onClick={() => setFilter({ ...filter, category_id: r.category_id === filter.category_id ? "" : r.category_id })}
                  className="w-full flex items-center justify-between gap-2 py-1.5 border-b border-slate-100 last:border-0 hover:bg-slate-50 -mx-2 px-2 rounded"
                >
                  <span className="flex items-center gap-2 min-w-0">
                    <span className="h-2 w-2 rounded-sm shrink-0" style={{ background: r.color }} />
                    <span className="text-[13px] truncate">{r.name}</span>
                  </span>
                  <span className="text-[11px] font-mono-fin">
                    {r.kind === "income"
                      ? <span className="text-green-700">↓ {formatRupeesCompact(r.in)}</span>
                      : <span className="text-red-600">↑ {formatRupeesCompact(r.out)}</span>}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" strokeWidth={1.75} />
          <Input
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search counterparty, reference, note…"
            className="pl-9 h-9" data-testid="cashbook-search"
          />
        </div>
        <Select value={filter.direction || "all"} onValueChange={(v) => setFilter({ ...filter, direction: v === "all" ? "" : v })}>
          <SelectTrigger className="w-[130px] h-9 text-[12px]" data-testid="filter-direction"><SelectValue placeholder="Direction" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All directions</SelectItem>
            <SelectItem value="in">Money in</SelectItem>
            <SelectItem value="out">Money out</SelectItem>
          </SelectContent>
        </Select>
        <FYPicker value={fy} onChange={(v) => { setFy(v); setMonth(""); }} size="sm" />
        <MonthPicker value={month} onChange={setMonth} fy={fy} size="sm" />
        <Select value={filter.investment_id || "all"} onValueChange={(v) => setFilter({ ...filter, investment_id: v === "all" ? "" : v })}>
          <SelectTrigger className="w-[190px] h-9 text-[12px]"><SelectValue placeholder="Investment" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All investments</SelectItem>
            {investments.map((i) => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filter.category_id || "all"} onValueChange={(v) => setFilter({ ...filter, category_id: v === "all" ? "" : v })}>
          <SelectTrigger className="w-[170px] h-9 text-[12px]"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        {activeFilterCount > 0 && (
          <Button variant="outline" size="sm" onClick={clearFilters} className="rounded-md h-9 gap-1 text-xs" data-testid="clear-filters">
            <X className="h-3 w-3" /> Clear ({activeFilterCount})
          </Button>
        )}
      </div>

      {/* Entries list — table on desktop, cards on mobile */}
      <div>
        <div className="eyebrow mb-3">
          Entries · {filteredEntries.length}
        </div>

        {/* Desktop table */}
        <div className="hidden md:block border border-slate-200 rounded-md overflow-x-auto thin-scroll bg-white">
          <table className="w-full text-[13px]">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                {["Date", "Type", "Account", "Category", "Investment", "Counterparty", "Amount", ""].map((h) => (
                  <th key={h} className={`p-3 text-[10px] tracking-wider uppercase text-slate-500 ${h === "Amount" ? "text-right" : "text-left"}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredEntries.map((e) => {
                const acc = accMap[e.account_id];
                const cat = catMap[e.category_id];
                const inv = invMap[e.investment_id];
                return (
                  <tr key={e.id} className="border-b border-slate-100 hover:bg-slate-50/70" data-testid={`entry-${e.id}`}>
                    <td className="p-3 text-slate-600 whitespace-nowrap">{formatDate(e.date)}</td>
                    <td className="p-3">
                      {e.direction === "in"
                        ? <span className="inline-flex items-center gap-1 text-green-700 text-[11px] font-medium"><ArrowDownLeft className="h-3 w-3" /> IN</span>
                        : <span className="inline-flex items-center gap-1 text-red-600 text-[11px] font-medium"><ArrowUpRight className="h-3 w-3" /> OUT</span>}
                    </td>
                    <td className="p-3 text-slate-700 truncate max-w-[140px]">{acc?.name || <span className="text-slate-400 italic">—</span>}</td>
                    <td className="p-3">
                      {cat ? (
                        <span className="inline-flex items-center gap-1.5 text-[12px]">
                          <span className="h-1.5 w-1.5 rounded-sm" style={{ background: cat.color }} />
                          {cat.name}
                        </span>
                      ) : <span className="text-slate-400 italic">—</span>}
                    </td>
                    <td className="p-3">
                      {inv ? (
                        <span className="inline-block text-[11px] px-1.5 py-0.5 bg-red-50 text-red-700 rounded max-w-[140px] truncate">{inv.name}</span>
                      ) : <span className="text-slate-400 italic text-[11px]">Untagged</span>}
                    </td>
                    <td className="p-3 text-slate-600 truncate max-w-[140px]">{e.counterparty || "—"}</td>
                    <td className={`p-3 text-right font-mono-fin font-medium whitespace-nowrap ${e.direction === "in" ? "text-green-700" : "text-red-600"}`}>
                      {e.direction === "in" ? "+" : "−"}{formatRupees(e.amount)}
                    </td>
                    <td className="p-3 text-right whitespace-nowrap">
                      {canEdit && (
                        <div className="flex items-center gap-1 justify-end">
                          <button onClick={() => openEdit(e)} className="p-1 text-slate-400 hover:text-slate-700"><Pencil className="h-3.5 w-3.5" /></button>
                          <button onClick={() => remove(e.id)} className="p-1 text-slate-400 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
              {filteredEntries.length === 0 && (
                <tr><td colSpan={8} className="p-10 text-center text-slate-500 text-sm">No entries match these filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile cards */}
        <div className="md:hidden grid gap-2">
          {filteredEntries.map((e) => {
            const acc = accMap[e.account_id];
            const cat = catMap[e.category_id];
            const inv = invMap[e.investment_id];
            return (
              <div key={e.id} className="border border-slate-200 rounded-md bg-white p-3.5" data-testid={`entry-mobile-${e.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-[11px] text-slate-500">{formatDate(e.date)} · {acc?.name}</div>
                    <div className="text-[13px] font-medium text-slate-900 mt-0.5 truncate">
                      {e.counterparty || cat?.name || "Entry"}
                    </div>
                    <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                      {cat && (
                        <span className="inline-flex items-center gap-1 text-[10px] text-slate-600">
                          <span className="h-1.5 w-1.5 rounded-sm" style={{ background: cat.color }} />
                          {cat.name}
                        </span>
                      )}
                      {inv && (
                        <span className="inline-block text-[10px] px-1 py-0.5 bg-red-50 text-red-700 rounded">{inv.name}</span>
                      )}
                    </div>
                  </div>
                  <div className={`font-mono-fin font-medium text-sm shrink-0 ${e.direction === "in" ? "text-green-700" : "text-red-600"}`}>
                    {e.direction === "in" ? "+" : "−"}{formatRupeesCompact(e.amount)}
                  </div>
                </div>
                {canEdit && (
                  <div className="mt-2 flex justify-end gap-3 text-[11px]">
                    <button onClick={() => openEdit(e)} className="text-slate-500">Edit</button>
                    <button onClick={() => remove(e.id)} className="text-red-600">Delete</button>
                  </div>
                )}
              </div>
            );
          })}
          {filteredEntries.length === 0 && (
            <div className="text-center text-slate-500 text-sm py-10 border border-dashed border-slate-200 rounded-md">No entries.</div>
          )}
        </div>
      </div>

      {/* Add/Edit dialog */}
      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit entry" : "Record cash / bank entry"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 max-h-[70vh] overflow-y-auto -mx-1 px-1">
            <div>
              <Label>Tag to investment (project)</Label>
              <Select value={form.investment_id || "none"} onValueChange={(v) => setForm({ ...form, investment_id: v === "none" ? "" : v, account_id: "", category_id: "", sub_category_id: "" })}>
                <SelectTrigger className="mt-2" data-testid="e-investment"><SelectValue placeholder="Choose project first — masters will filter accordingly" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Not tagged (all masters) —</SelectItem>
                  {investments.map((i) => (
                    <SelectItem key={i.id} value={i.id}>
                      {i.name} <span className="text-slate-400">({INVESTMENT_TYPE_LABEL[i.type]})</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {form.investment_id && (
                <div className="text-[10px] text-slate-400 mt-1">Only masters applicable to this project are shown below.</div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Direction</Label>
                <Select value={form.direction} onValueChange={(v) => setForm({ ...form, direction: v })}>
                  <SelectTrigger className="mt-2" data-testid="e-direction"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="in">Money in (received)</SelectItem>
                    <SelectItem value="out">Money out (paid)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Date</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-2" data-testid="e-date" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Amount (Rs)</Label>
                <Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-2" data-testid="e-amount" />
              </div>
              <div>
                <Label>Account</Label>
                <Select value={form.account_id} onValueChange={(v) => setForm({ ...form, account_id: v })}>
                  <SelectTrigger className="mt-2" data-testid="e-account"><SelectValue placeholder="Choose account" /></SelectTrigger>
                  <SelectContent>
                    {dlgAccounts.map((a) => (
                      <SelectItem key={a.id} value={a.id}>
                        {a.name} {a.account_number && `· ${a.account_number}`}
                      </SelectItem>
                    ))}
                    {dlgAccounts.length === 0 && (
                      <div className="px-2 py-2 text-[11px] text-slate-400">No accounts tagged to this project.</div>
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Category / Head</Label>
                <Select value={form.category_id || "none"} onValueChange={(v) => setForm({ ...form, category_id: v === "none" ? "" : v, sub_category_id: "" })}>
                  <SelectTrigger className="mt-2" data-testid="e-category"><SelectValue placeholder="Choose category" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None —</SelectItem>
                    {dlgCategories
                      .filter((c) => form.direction === "in" ? c.kind !== "expense" : c.kind !== "income")
                      .map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          <span className="inline-flex items-center gap-2">
                            <span className="h-2 w-2 rounded-sm" style={{ background: c.color }} /> {c.name}
                          </span>
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Sub-category</Label>
                <Select
                  value={form.sub_category_id || "none"}
                  onValueChange={(v) => setForm({ ...form, sub_category_id: v === "none" ? "" : v })}
                  disabled={!form.category_id}
                >
                  <SelectTrigger className="mt-2" data-testid="e-subcategory"><SelectValue placeholder={form.category_id ? "Optional" : "Choose category first"} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None —</SelectItem>
                    {dlgSubs.filter((s) => s.category_id === form.category_id).map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Counterparty (who)</Label>
                <Input value={form.counterparty} onChange={(e) => setForm({ ...form, counterparty: e.target.value })} placeholder="Suresh Menon / HDFC / vendor" className="mt-2" data-testid="e-counterparty" />
              </div>
              <div>
                <Label>Reference (cheque / UTR)</Label>
                <Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} placeholder="UTR/cheque no." className="mt-2" data-testid="e-reference" />
              </div>
            </div>
            <div>
              <Label>Note</Label>
              <Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="mt-2" rows={2} data-testid="e-note" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(false)} className="rounded-md">Cancel</Button>
            <Button onClick={save} className="rounded-md bg-red-600 hover:bg-red-700 text-white" data-testid="e-save">
              {editing ? "Update" : "Save entry"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ImportDialog
        open={importOpen} onOpenChange={setImportOpen}
        accounts={accounts} categories={categories} investments={investments}
        onDone={() => { setImportOpen(false); load(); loadEntries(); }}
      />

      <ReconcileDialog
        account={reconcileFor} onClose={() => setReconcileFor(null)}
        onDone={() => { setReconcileFor(null); load(); loadEntries(); }}
      />
    </div>
  );
}

/* -------------------- Import wizard -------------------- */
function ImportDialog({ open, onOpenChange, accounts, categories, investments, onDone }) {
  const [step, setStep] = useState(1); // 1 = choose+upload, 2 = review
  const [accountId, setAccountId] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  const reset = () => { setStep(1); setAccountId(""); setFile(null); setPreview(null); setRows([]); };
  useEffect(() => { if (!open) reset(); }, [open]);

  const catMap = Object.fromEntries(categories.map((c) => [c.id, c]));
  const invMap = Object.fromEntries(investments.map((i) => [i.id, i]));

  const runPreview = async () => {
    if (!accountId || !file) return toast.error("Choose account and file");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/book/import/preview?account_id=${accountId}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(r.data);
      setRows(r.data.rows.map((x) => ({
        ...x,
        category_id: x.suggested_category_id || "",
        investment_id: x.suggested_investment_id || "",
        counterparty: x.suggested_counterparty || "",
        _skip: false,
      })));
      setStep(2);
    } catch (e) { toast.error(e?.response?.data?.detail || "Preview failed"); }
    finally { setBusy(false); }
  };

  const commit = async () => {
    setBusy(true);
    try {
      const payload = {
        account_id: accountId,
        rows: rows.filter((r) => !r._skip).map((r) => ({
          date: r.date, direction: r.direction, amount: r.amount,
          description: r.description, reference: r.reference,
          category_id: r.category_id || null,
          investment_id: r.investment_id || null,
          counterparty: r.counterparty || "",
          note: r.description,
        })),
      };
      const r = await api.post("/book/import/commit", payload);
      toast.success(`Imported ${r.data.created} entries`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Import failed"); }
    finally { setBusy(false); }
  };

  const setRow = (idx, patch) => setRows((rs) => rs.map((r, i) => i === idx ? { ...r, ...patch } : r));
  const toKeepCount = rows.filter((r) => !r._skip).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Import bank statement</DialogTitle>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-4">
            <div className="text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-md p-3">
              1. Download the template (Cash & Bank page → Template).<br />
              2. Paste your bank statement into it (or upload your bank&apos;s XLS / XLSX / CSV export directly).<br />
              3. We&apos;ll match categories and investments automatically. You can adjust before importing.
            </div>
            <div>
              <Label>Which account does this statement belong to?</Label>
              <Select value={accountId} onValueChange={setAccountId}>
                <SelectTrigger className="mt-2" data-testid="import-account"><SelectValue placeholder="Choose account" /></SelectTrigger>
                <SelectContent>
                  {accounts.map((a) => (
                    <SelectItem key={a.id} value={a.id}>{a.name} {a.account_number && `· ${a.account_number}`}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Statement file (.xlsx, .xls, .csv)</Label>
              <input
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={(e) => setFile(e.target.files?.[0])}
                data-testid="import-file"
                className="mt-2 block w-full text-sm text-slate-600 file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:bg-slate-900 file:text-white file:text-[13px] file:cursor-pointer"
              />
              {file && <div className="text-xs text-slate-500 mt-2">Selected: {file.name}</div>}
            </div>
          </div>
        )}

        {step === 2 && preview && (
          <div>
            <div className="text-xs text-slate-500 mb-3">
              <span className="font-medium text-slate-900">{preview.total_rows}</span> rows parsed from <span className="font-medium text-slate-900">{preview.account.name}</span> · adjust categories and investment tags below.
            </div>
            <div className="border border-slate-200 rounded-md max-h-[420px] overflow-auto thin-scroll">
              <table className="w-full text-[12px]">
                <thead className="bg-slate-50 border-b border-slate-200 sticky top-0">
                  <tr>
                    {["", "Date", "Description", "Amount", "Category", "Investment"].map((h, i) => (
                      <th key={i} className="p-2 text-left text-[10px] tracking-wider uppercase text-slate-500 font-medium whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i} className={`border-b border-slate-100 ${r._skip ? "opacity-40" : ""}`}>
                      <td className="p-2">
                        <input type="checkbox" checked={!r._skip} onChange={(e) => setRow(i, { _skip: !e.target.checked })} />
                      </td>
                      <td className="p-2 whitespace-nowrap text-slate-700">{formatDate(r.date)}</td>
                      <td className="p-2 text-slate-700 max-w-[240px]">
                        <div className="truncate" title={r.description}>{r.description}</div>
                        <div className="text-[10px] text-slate-400">{r.reference}</div>
                      </td>
                      <td className={`p-2 text-right font-mono-fin whitespace-nowrap ${r.direction === "in" ? "text-green-700" : "text-red-600"}`}>
                        {r.direction === "in" ? "+" : "−"}{formatRupeesCompact(r.amount)}
                      </td>
                      <td className="p-2">
                        <select
                          value={r.category_id || ""}
                          onChange={(e) => setRow(i, { category_id: e.target.value })}
                          className="text-[11px] border border-slate-200 rounded px-1 py-0.5 max-w-[140px] bg-white"
                        >
                          <option value="">—</option>
                          {categories
                            .filter((c) => r.direction === "in" ? c.kind !== "expense" : c.kind !== "income")
                            .map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                      </td>
                      <td className="p-2">
                        <select
                          value={r.investment_id || ""}
                          onChange={(e) => setRow(i, { investment_id: e.target.value })}
                          className="text-[11px] border border-slate-200 rounded px-1 py-0.5 max-w-[140px] bg-white"
                        >
                          <option value="">— Untagged —</option>
                          {investments.map((inv) => <option key={inv.id} value={inv.id}>{inv.name}</option>)}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-2 text-xs text-slate-500">
              {toKeepCount} of {rows.length} rows will be imported.
            </div>
          </div>
        )}

        <DialogFooter>
          {step === 1 ? (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-md">Cancel</Button>
              <Button onClick={runPreview} disabled={busy || !accountId || !file} className="rounded-md bg-red-600 hover:bg-red-700 text-white gap-2" data-testid="import-preview-btn">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Preview
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => setStep(1)} className="rounded-md">Back</Button>
              <Button onClick={commit} disabled={busy || toKeepCount === 0} className="rounded-md bg-red-600 hover:bg-red-700 text-white gap-2" data-testid="import-commit-btn">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Import {toKeepCount} rows
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* -------------------- Reconcile dialog -------------------- */
function ReconcileDialog({ account, onClose, onDone }) {
  const [actual, setActual] = useState("");
  const [asOf, setAsOf] = useState(todayISO());
  const [note, setNote] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (account) {
      setActual(""); setAsOf(todayISO()); setNote(""); setResult(null);
    }
  }, [account]);

  const check = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/accounts/${account.id}/reconcile`, {
        actual_balance: Number(actual), as_of_date: asOf, note,
        record_adjustment: false,
      });
      setResult(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const commit = async () => {
    setBusy(true);
    try {
      await api.post(`/accounts/${account.id}/reconcile`, {
        actual_balance: Number(actual), as_of_date: asOf, note,
        record_adjustment: true,
      });
      toast.success("Adjustment recorded");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const diff = result?.difference || 0;
  const matched = result && Math.abs(diff) < 0.5;

  return (
    <Dialog open={!!account} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reconcile {account?.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Actual balance (from bank)</Label>
              <Input type="number" value={actual} onChange={(e) => setActual(e.target.value)} className="mt-2" data-testid="rec-actual" />
            </div>
            <div>
              <Label>As of date</Label>
              <Input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} className="mt-2" />
            </div>
          </div>
          <div>
            <Label>Note (optional)</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. checked HDFC statement, missing 2 UPI txns" className="mt-2" />
          </div>

          {result && (
            <div className={`p-4 rounded-md border ${matched ? "border-green-200 bg-green-50" : "border-yellow-300 bg-yellow-50"}`}>
              <div className="grid grid-cols-3 gap-3 text-[13px]">
                <div>
                  <div className="text-[10px] tracking-wider uppercase text-slate-500">System says</div>
                  <div className="font-mono-fin text-slate-900 mt-1">{formatRupees(result.system_balance)}</div>
                </div>
                <div>
                  <div className="text-[10px] tracking-wider uppercase text-slate-500">Bank says</div>
                  <div className="font-mono-fin text-slate-900 mt-1">{formatRupees(result.actual_balance)}</div>
                </div>
                <div>
                  <div className="text-[10px] tracking-wider uppercase text-slate-500">Difference</div>
                  <div className={`font-mono-fin font-medium mt-1 ${matched ? "text-green-700" : diff > 0 ? "text-slate-900" : "text-red-600"}`}>
                    {matched ? "0" : (diff > 0 ? "+" : "") + formatRupees(diff)}
                  </div>
                </div>
              </div>
              {!matched && (
                <div className="mt-3 text-xs text-slate-700 leading-relaxed">
                  {diff > 0
                    ? <>Bank has <b>{formatRupees(diff)} more</b> than the system. Recording will add an income adjustment.</>
                    : <>Bank has <b>{formatRupees(-diff)} less</b>. Recording will add an expense adjustment (likely a fee or missed entry).</>}
                </div>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-md">Close</Button>
          {!result && (
            <Button onClick={check} disabled={busy || !actual} className="rounded-md bg-slate-900 hover:bg-slate-800 text-white" data-testid="rec-check">
              {busy ? "Checking…" : "Compare"}
            </Button>
          )}
          {result && !matched && (
            <Button onClick={commit} disabled={busy} className="rounded-md bg-red-600 hover:bg-red-700 text-white" data-testid="rec-commit">
              Record adjustment
            </Button>
          )}
          {result && matched && (
            <Button onClick={onClose} className="rounded-md bg-green-600 hover:bg-green-700 text-white gap-2">
              <Check className="h-4 w-4" /> All matched
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
